import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { useInstantSearch } from 'react-instantsearch';

/* Components */
import SearchRecents from './SearchRecents';
import SearchSection from './SearchSection';
import PageHits from './PageHits';
import ModelHits from './ModelHits';
import VehicleHits from './VehicleHits';
import EstablishmentHits from './EstablishmentHits';
import PlannerHits from './PlannerHits';
import { ChevronIconRight } from '../../common/Icons';

function NoResults() {
    const { scopedResults } = useInstantSearch();

    const totalHits = scopedResults.reduce((sum, result) => {
        return sum + (result.results?.nbHits || 0);
    }, 0);

    if (totalHits > 0) return null;

    return (
        <div className="col-12">
            <p className="text-muted">{window.Translator.trans('app.header.search.results.no_results')}</p>
        </div>
    );
}

export default function SearchResults({ inputValue, setInputValue, recents, typesenseConfig, suluSearchData, swiperPagination }) {
    const collections = typesenseConfig?.collections || {};
    const hasQuery = inputValue && inputValue.length > 1;
    const brand = suluSearchData?.brand || '';
    const webspace = suluSearchData?.webspace || 'hedinautomotive';
    const locale = suluSearchData?.locale || 'nl';
    const isNL = locale === 'nl' || locale.startsWith('nl');
    const vehicleBrandFilter = brand ? `brand:=${brand}` : '';
    const pageWebspaceFilter = webspace !== 'hedinautomotive' ? `webspace:=${webspace}` : '';

    return (
        <AnimatePresence initial={false} exitBeforeEnter>
            <div className="row gutters-5 gutters-lg-10 row--horizontal-bordered row--lg-horizontal-unbordered">
                <div className="col-12 col-lg-3">
                    {hasQuery && (
                        <div className="row gutters-6 gutters-lg-14 mb-6 mb-lg-14 row--horizontal-bordered row--lg-horizontal-unbordered">
                            <SearchSection indexName={collections.pages} indexId="pages" config={{ hitsPerPage: 5, filters: [`type:[default,vehiclelist]`, pageWebspaceFilter].filter(Boolean).join(' && ') }}>
                                <PageHits />
                            </SearchSection>

                            <SearchSection indexName={collections.pages} indexId="models" config={{ hitsPerPage: 5, filters: [`type:model`, pageWebspaceFilter].filter(Boolean).join(' && ') }}>
                                <ModelHits
                                    modelOverviewUrl={suluSearchData?.model_overview_url}
                                    modelOverviewTitle={suluSearchData?.model_overview_title}
                                />
                            </SearchSection>
                        </div>
                    )}

                    <div className="row gutters-5 gutters-lg-10 row--horizontal-bordered row--lg-horizontal-unbordered">
                        <SearchRecents
                            query={inputValue}
                            setQuery={setInputValue}
                            recents={recents}
                            hasResults={hasQuery}
                        />
                        {suluSearchData?.more_links?.length > 0 &&
                            <div className="col-12 col-md-6 col-lg-12">
                                <p className="font-weight-bold mb-5">{window.Translator.trans('app.header.search.results.more_for_you')}</p>
                                <div className="row row--equalized gutters-2 gutters-lg-4">
                                    {suluSearchData?.more_links.map((linkItem, index) => (
                                        <div key={index} className="col-12">
                                            <a className="text-decoration-none" href={linkItem?.href}>{linkItem?.title}</a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }
                    </div>
                </div>
                <div className="col-12 col-lg-9">
                    <div className="row gutters-6 gutters-lg-14">
                        {!hasQuery && suluSearchData?.popular_items?.length > 0 &&
                            <div className="col-12">
                                <p className="font-weight-bold mb-5">{window.Translator.trans('app.header.search.results.popular')}</p>
                                <div className="row row--equalized gutters-2 gutters-lg-4">
                                    {suluSearchData?.popular_items.map((popularItem, index) => (
                                        <div key={index} className="col-12 col-md-6 col-lg-4">
                                            <div className="card bg-white">
                                                <div className="card-body d-flex flex-column p-4 p-lg-8">
                                                    <div className="row gutters-4 align-items-start">
                                                        <div className="col">
                                                            <div>
                                                                {popularItem?.title}
                                                            </div>
                                                        </div>
                                                        <div className="col-auto">
                                                            <div className="ml-auto text-right mt-auto">
                                                                <a className="btn btn--circle text-base p-0" title={popularItem?.title} href={popularItem?.href}>
                                                                    <ChevronIconRight className="icon" />
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }

                        {hasQuery && (
                            <NoResults />
                        )}

                        {hasQuery && (
                            <SearchSection indexName={collections.vehicles} config={{ hitsPerPage: 3, ...(vehicleBrandFilter && { filters: vehicleBrandFilter }) }}>
                                <VehicleHits
                                    vehicleOverviewUrl={suluSearchData?.vehicle_overview_url}
                                    swiperPagination={swiperPagination}
                                    query={inputValue}
                                />
                            </SearchSection>
                        )}

                        {hasQuery && collections.establishments && (
                            <SearchSection indexName={collections.establishments} config={{ hitsPerPage: 20 }}>
                                <EstablishmentHits />
                            </SearchSection>
                        )}

                        {hasQuery && collections.wpp && isNL && (
                            <SearchSection indexName={collections.wpp} config={{ hitsPerPage: 100 }}>
                                <PlannerHits />
                            </SearchSection>
                        )}
                    </div>
                </div>
            </div>
        </AnimatePresence>
    );
}
